import type { ReactNode } from 'react';
import { Bookmark, Heart, MessageCircle, MoreHorizontal, Music, Plus, Share2 } from 'lucide-react';

import { BrandAvatar } from './BrandAvatar';
import { ExpandableCaption } from './ExpandableCaption';
import { PhoneFrame, StatusBar } from './PhoneFrame';
import { PreviewMedia } from './PreviewMedia';
import { useTranslation } from 'react-i18next';

interface TikTokPreviewProps {
  caption: string;
  mediaUrl?: string;
  mediaType?: 'image' | 'video';
  mediaSlot?: ReactNode;
  displayName: string;
  handle: string;
  avatarUrl: string | null;
}

const ActionStat = ({
  icon,
  label,
  filled,
}: {
  icon: React.ReactNode;
  label: string;
  filled?: boolean;
}) => (
  <div className="flex flex-col items-center gap-1">
    <div
      className={`text-white drop-shadow-[0_1px_2px_rgba(0,0,0,0.5)] ${
        filled ? '[&_svg]:fill-white' : ''
      }`}
    >
      {icon}
    </div>
    <span className="text-xs font-semibold text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]">
      {label}
    </span>
  </div>
);

export const TikTokPreview = ({
  caption,
  mediaUrl,
  mediaType,
  mediaSlot,
  displayName,
  handle,
  avatarUrl,
}: TikTokPreviewProps) => {
  const { t } = useTranslation('contentTest');
  return (
    <PhoneFrame bgcolor="#000">
      {mediaSlot ??
        (mediaUrl ? (
          <PreviewMedia
            mediaUrl={mediaUrl}
            mediaType={mediaType}
            className="absolute inset-0 w-full h-full object-cover z-0"
          />
        ) : null)}

      <div className="relative z-10 text-white">
        <StatusBar tone="light" />
      </div>

      <div className="relative z-10 flex flex-1 flex-col text-white">
        <div className="relative flex items-center justify-center gap-6 pt-2 pb-2">
          <span className="text-[15px] font-semibold text-white/70 [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]">
            {t('contentTest.tikTokPreview.following')}
          </span>
          <div className="flex flex-col items-center">
            <span className="text-base font-bold text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]">
              {t('contentTest.tikTokPreview.forYou')}
            </span>
            <div className="w-6 h-0.5 bg-white mt-0.5" />
          </div>
          <div className="absolute right-3 top-2 text-white">
            <MoreHorizontal size={20} />
          </div>
        </div>

        {!mediaUrl && !mediaSlot && (
          <div className="flex flex-1 flex-col items-center justify-center gap-2">
            <Music size={56} color="rgba(255,255,255,0.6)" />
            <p className="text-base text-white/60">
              {t('contentTest.tikTokPreview.uploadMediaToPreview')}
            </p>
          </div>
        )}

        <div className="flex-1" />

        {/* Bottom-up scrim so the caption stays legible over busy media, even
          when expanded — the video still shows through the transparent top.
          Matches TikTok's own gradient behind the caption. */}
        <div
          data-testid="phone-preview-caption-scrim"
          className="relative z-10 bg-gradient-to-t from-black/80 via-black/40 to-transparent px-3 pt-10 pb-4"
        >
          <p className="text-[15px] font-bold text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]">
            @{handle}
          </p>
          <ExpandableCaption
            data-testid="phone-preview-caption"
            className="text-sm text-white mt-1 pr-12 [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]"
            toggleClassName="mt-0.5 block text-sm font-semibold text-white/80 [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]"
            // TikTok's frame can't scroll, so an expanded caption scrolls within a
            // bounded height here instead of overflowing and clipping the frame.
            expandedClassName="block max-h-72 overflow-y-auto [scrollbar-width:thin]"
          >
            {caption}
          </ExpandableCaption>
          <div className="flex items-center gap-1 mt-2 text-white">
            <Music size={14} />
            <span className="text-xs text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.5)]">
              {t('contentTest.tikTokPreview.originalSound', { displayName })}
            </span>
          </div>
        </div>
      </div>

      <div className="absolute right-2.5 bottom-24 z-20 flex flex-col items-center gap-5">
        <div className="relative">
          <BrandAvatar avatarUrl={avatarUrl} size={44} alt={displayName} />
          <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-5 h-5 rounded-full bg-[#fe2c55] text-white flex items-center justify-center">
            <Plus size={14} strokeWidth={3} />
          </div>
        </div>
        <ActionStat
          icon={<Heart size={32} />}
          label={t('contentTest.tikTokPreview.n8425K')}
          filled
        />
        <ActionStat icon={<MessageCircle size={32} />} label="2,841" filled />
        <ActionStat
          icon={<Bookmark size={32} />}
          label={t('contentTest.tikTokPreview.n452K')}
          filled
        />
        <ActionStat icon={<Share2 size={32} />} label={t('contentTest.tikTokPreview.n125K')} />
      </div>
    </PhoneFrame>
  );
};

TikTokPreview.displayName = 'TikTokPreview';
